
import React, { useState, useEffect } from 'react';
import { WEEK_DAYS } from '../types';
import { getScheduleApi, updateScheduleApi } from '../api/scheduleApi';
import { getTechniciansApi } from '../api/techniciansApi';

type ScheduleType = 'US' | 'Xray';

interface TechnicianAssignment {
  technicianId: string;
  technicianName: string;
  defaultCapacity: number;
  intervalMinutes: number; // Appointment interval in minutes
  effectiveDate: string; // ISO date string YYYY-MM-DD - when this assignment becomes active
  slotCapacityOverrides?: { [time: string]: number };
}

interface WorkingHours {
  startTime: string;
  endTime: string;
  technicianAssignments?: TechnicianAssignment[]; // Sorted by effectiveDate DESC (newest first)
}

interface DayWorkingHours {
  dayId: string;
  dayLabel: string;
  isOpen: boolean;
  workingHours: WorkingHours[];
}

// Technician interface matching API structure
interface Technician {
  id: string;
  name: string;
  supportedServiceType: 'US' | 'Xray'; // Which services they support
  defaultConcurrency: number;
  isActive: boolean;
}

const Schedule: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ScheduleType>('US');
  const [usSchedule, setUsSchedule] = useState<DayWorkingHours[]>([]);
  const [xraySchedule, setXraySchedule] = useState<DayWorkingHours[]>([]);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [savingData, setSavingData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isFirstTime, setIsFirstTime] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showTechModal, setShowTechModal] = useState(false);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState<number | null>(null);
  const [showSlotCapacityModal, setShowSlotCapacityModal] = useState(false);
  const [editingTechnician, setEditingTechnician] = useState<{dayId: string, periodIndex: number} | null>(null);
  const [showEffectiveDateModal, setShowEffectiveDateModal] = useState(false);
  const [selectedTechnicianForAssignment, setSelectedTechnicianForAssignment] = useState<Technician | null>(null);
  const [effectiveDate, setEffectiveDate] = useState<string>('');
  const [intervalMinutes, setIntervalMinutes] = useState<number>(5);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Get available technicians for current tab
  const availableTechnicians = technicians.filter(
    t => t.isActive && t.supportedServiceType === activeTab
  );

  // Toggle technician section expansion
  const toggleTechnicianSection = (dayId: string, periodIndex: number) => {
    const key = `${dayId}-${periodIndex}`;
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const isTechnicianSectionExpanded = (dayId: string, periodIndex: number) => {
    const key = `${dayId}-${periodIndex}`;
    return expandedSections.has(key);
  };

  // Helper: Get active technician assignment for a given date
  const getActiveTechnicianForDate = (assignments: TechnicianAssignment[] | undefined, date: string): TechnicianAssignment | null => {
    if (!assignments || assignments.length === 0) return null;
    
    // Find the most recent assignment that is <= date
    const sorted = [...assignments].sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate));
    return sorted.find(a => a.effectiveDate <= date) || null;
  };

  // Helper: Get current active technician (for today)
  const getCurrentTechnician = (period: WorkingHours): TechnicianAssignment | null => {
    const today = new Date().toISOString().split('T')[0];
    return getActiveTechnicianForDate(period.technicianAssignments, today);
  };

  // Helper: Get future technician changes
  const getFutureTechnicianChanges = (period: WorkingHours): TechnicianAssignment[] => {
    if (!period.technicianAssignments) return [];
    const today = new Date().toISOString().split('T')[0];
    return period.technicianAssignments.filter(a => a.effectiveDate > today)
      .sort((a, b) => a.effectiveDate.localeCompare(b.effectiveDate));
  };

  // Get current schedule based on active tab
  const schedule = activeTab === 'US' ? usSchedule : xraySchedule;
  const setSchedule = activeTab === 'US' 
    ? (data: DayWorkingHours[] | ((prev: DayWorkingHours[]) => DayWorkingHours[])) => {
        if (typeof data === 'function') {
          setUsSchedule(data);
        } else {
          setUsSchedule(data);
        }
      }
    : (data: DayWorkingHours[] | ((prev: DayWorkingHours[]) => DayWorkingHours[])) => {
        if (typeof data === 'function') {
          setXraySchedule(data);
        } else {
          setXraySchedule(data);
        }
      };

  useEffect(() => {
    loadTechnicians();
    loadSchedule();
  }, [activeTab]); // Reload when tab changes

  // Reset unsavedChanges when tab changes
  useEffect(() => {
    setHasUnsavedChanges(false);
  }, [activeTab]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const loadTechnicians = async () => {
    try {
      const data = await getTechniciansApi();
      setTechnicians(data);
    } catch (error) {
      console.error('Error loading technicians:', error);
    }
  };

  const loadSchedule = async () => {
    try {
      setLoadingData(true);
      setError(null);
      const scheduleType = activeTab === 'US' ? 2 : 1; // USType=2, XrayType=1
      const data = await getScheduleApi(scheduleType);
      
      // If no schedule exists in DB (empty or first time), show default and prompt to save
      if (!data || data.length === 0) {
        console.log(`No ${activeTab} schedule found in database, showing default schedule...`);
        const defaultSchedule = WEEK_DAYS.map(day => ({
          dayId: day.id,
          dayLabel: day.label,
          isOpen: day.id !== 'Sunday' && day.id !== 'Saturday',
          workingHours: [
            { 
              startTime: '08:00', 
              endTime: '12:00',
              technicianAssignments: []
            },
            { 
              startTime: '14:00', 
              endTime: '18:00',
              technicianAssignments: []
            }
          ]
        }));
        
        setSchedule(defaultSchedule);
        setIsFirstTime(true);
      } else {
        // Convert API data to new format if needed
        const convertedData = data.map((day: any) => ({
          ...day,
          // Ensure workingHours is always an array
          workingHours: (day.workingHours || []).map((period: any) => {
            // Check if it's old format (single technician) or new format (assignments array)
            if (period.technicianAssignments) {
              // Already new format
              return period;
            } else if (period.technicianId) {
              // Old format - convert to new
              return {
                startTime: period.startTime,
                endTime: period.endTime,
                technicianAssignments: [{
                  technicianId: period.technicianId,
                  technicianName: period.technicianName,
                  defaultCapacity: period.defaultCapacity || 1,
                  intervalMinutes: period.intervalMinutes || 5,
                  effectiveDate: new Date().toISOString().split('T')[0], // Set to today
                  slotCapacityOverrides: period.slotCapacityOverrides
                }]
              };
            } else {
              // No technician assigned
              return {
                startTime: period.startTime,
                endTime: period.endTime,
                technicianAssignments: []
              };
            }
          })
        }));
        setSchedule(convertedData);
        setIsFirstTime(false);
      }
    } catch (err) {
      console.error('Failed to load schedule:', err);
      setError('טעינת לוח הזמנים נכשלה');
    } finally {
      setLoadingData(false);
    }
  };

  const handleSaveSchedule = async () => {
    try {
      setSavingData(true);
      setError(null);
      setSaveSuccess(false);
      const scheduleType = activeTab === 'US' ? 2 : 1; // USType=2, XrayType=1
      await updateScheduleApi(schedule, scheduleType);
      setIsFirstTime(false); // No longer first time after saving
      setHasUnsavedChanges(false); // Reset after successful save
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save schedule:', err);
      setError('שמירת לוח הזמנים נכשלה');
    } finally {
      setSavingData(false);
    }
  };

  const toggleDay = (dayId: string) => {
    setSchedule(prev => prev.map(day => {
      if (day.dayId === dayId) {
        const newIsOpen = !day.isOpen;
        
        // If closing the day, clear workingHours
        if (!newIsOpen) {
          return { ...day, isOpen: newIsOpen, workingHours: [] };
        }
        
        // If opening the day and workingHours is empty, initialize with defaults
        if (newIsOpen && day.workingHours && day.workingHours.length === 0) {
          return {
            ...day,
            isOpen: newIsOpen,
            workingHours: [
              { 
                startTime: '08:00', 
                endTime: '12:00',
                technicianAssignments: []
              },
              { 
                startTime: '14:00', 
                endTime: '18:00',
                technicianAssignments: []
              }
            ]
          };
        }
        
        // Otherwise just toggle isOpen and keep existing workingHours
        return { ...day, isOpen: newIsOpen };
      }
      return day;
    }));
    setHasUnsavedChanges(true);
  };

  const updateWorkingHours = (dayId: string, index: number, field: 'startTime' | 'endTime', value: string) => {
    setSchedule(prev => prev.map(day => {
      if (day.dayId === dayId) {
        const newWorkingHours = [...day.workingHours];
        newWorkingHours[index] = { ...newWorkingHours[index], [field]: value };
        return { ...day, workingHours: newWorkingHours };
      }
      return day;
    }));
    setHasUnsavedChanges(true);
  };

  const addWorkingPeriod = (dayId: string) => {
    setSchedule(prev => prev.map(day => {
      if (day.dayId === dayId) {
        const workingHours = day.workingHours || [];
        const lastPeriod = workingHours[workingHours.length - 1];
        const newStart = lastPeriod ? lastPeriod.endTime : '09:00';
        return {
          ...day,
          workingHours: [...workingHours, {
            startTime: newStart, 
            endTime: '17:00',
            technicianAssignments: []
          }]
        };
      }
      return day;
    }));
    setHasUnsavedChanges(true);
  };

  const removeWorkingPeriod = (dayId: string, index: number) => {
    setSchedule(prev => prev.map(day => {
      if (day.dayId === dayId && day.workingHours.length > 1) {
        return {
          ...day,
          workingHours: day.workingHours.filter((_, i) => i !== index)
        };
      }
      return day;
    }));
    setHasUnsavedChanges(true);
  };

  const openTechnicianModal = (dayId: string, periodIndex: number) => {
    setSelectedDay(dayId);
    setSelectedPeriodIndex(periodIndex);
    setShowTechModal(true);
  };

  const selectTechnicianForAssignment = (technician: Technician) => {
    setSelectedTechnicianForAssignment(technician);
    setShowTechModal(false);
    
    // Default to today's date and 5 minutes interval
    const today = new Date().toISOString().split('T')[0];
    setEffectiveDate(today);
    setIntervalMinutes(5);
    setShowEffectiveDateModal(true);
  };

  const confirmTechnicianAssignment = () => {
    if (!selectedDay || selectedPeriodIndex === null || !selectedTechnicianForAssignment || !effectiveDate) return;

    setSchedule(prev => prev.map(day => {
      if (day.dayId === selectedDay) {
        const newWorkingHours = [...day.workingHours];
        const period = newWorkingHours[selectedPeriodIndex];
        
        const newAssignment: TechnicianAssignment = {
          technicianId: selectedTechnicianForAssignment.id,
          technicianName: selectedTechnicianForAssignment.name,
          defaultCapacity: selectedTechnicianForAssignment.defaultConcurrency,
          intervalMinutes: intervalMinutes,
          effectiveDate: effectiveDate,
          slotCapacityOverrides: undefined
        };

        // Remove existing assignment with the same effectiveDate (replace, not duplicate)
        const existingAssignments = (period.technicianAssignments || [])
          .filter(a => a.effectiveDate !== effectiveDate);
        
        // Add new assignment and sort by date DESC
        period.technicianAssignments = [newAssignment, ...existingAssignments]
          .sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate));

        return { ...day, workingHours: newWorkingHours };
      }
      return day;
    }));
    
    setHasUnsavedChanges(true);
    setShowEffectiveDateModal(false);
    setSelectedTechnicianForAssignment(null);
    setEffectiveDate('');
    setIntervalMinutes(5);
    
    // Auto-expand the technician section
    if (selectedDay && selectedPeriodIndex !== null) {
      const key = `${selectedDay}-${selectedPeriodIndex}`;
      setExpandedSections(prev => new Set(prev).add(key));
    }
  };

  const removeTechnicianAssignment = (dayId: string, periodIndex: number, effectiveDate: string) => {
    setSchedule(prev => prev.map(day => {
      if (day.dayId === dayId) {
        const newWorkingHours = [...day.workingHours];
        const period = newWorkingHours[periodIndex];
        
        // Remove the specific assignment
        period.technicianAssignments = (period.technicianAssignments || [])
          .filter(a => a.effectiveDate !== effectiveDate);
        
        return { ...day, workingHours: newWorkingHours };
      }
      return day;
    }));
    setHasUnsavedChanges(true);
  };

  const openSlotCapacityModal = (dayId: string, periodIndex: number) => {
    // Can only edit slots for current technician
    const day = schedule.find(d => d.dayId === dayId);
    const period = day?.workingHours[periodIndex];
    const currentTech = getCurrentTechnician(period!);
    
    if (!currentTech) {
      alert('אין טכנאי מוקצה כרגע לתקופה זו');
      return;
    }
    
    setEditingTechnician({ dayId, periodIndex });
    setShowSlotCapacityModal(true);
  };

  const updateSlotCapacity = (dayId: string, periodIndex: number, timeSlot: string, capacity: number | undefined) => {
    setSchedule(prev => prev.map(day => {
      if (day.dayId === dayId) {
        const newWorkingHours = [...day.workingHours];
        const period = newWorkingHours[periodIndex];
        const currentTech = getCurrentTechnician(period);
        
        if (!currentTech) return day;
        
        // Update the current technician's slot overrides
        const assignments = period.technicianAssignments || [];
        const updatedAssignments = assignments.map(a => {
          if (a.effectiveDate === currentTech.effectiveDate) {
            const newOverrides = { ...(a.slotCapacityOverrides || {}) };
            if (capacity === undefined) {
              delete newOverrides[timeSlot];
            } else {
              newOverrides[timeSlot] = capacity;
            }
            return {
              ...a,
              slotCapacityOverrides: Object.keys(newOverrides).length > 0 ? newOverrides : undefined
            };
          }
          return a;
        });
        
        period.technicianAssignments = updatedAssignments;
        return { ...day, workingHours: newWorkingHours };
      }
      return day;
    }));
    setHasUnsavedChanges(true);
  };

  // Generate time slots for a period
  const generateTimeSlots = (startTime: string, endTime: string, intervalMinutes: number): string[] => {
    const slots: string[] = [];
    const start = new Date(`1970-01-01T${startTime}:00`);
    const end = new Date(`1970-01-01T${endTime}:00`);
    
    let current = new Date(start);
    while (current < end) {
      slots.push(current.toTimeString().substring(0, 5));
      current.setMinutes(current.getMinutes() + intervalMinutes);
    }
    
    return slots;
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-slate-800">ניהול שעות עבודה והקצאת טכנאים</h2>
        <p className="text-slate-500 mt-1">הגדר שעות פעילות והקצה טכנאים לכל יום בשבוע</p>
      </div>

      {/* Tabs for Schedule Types */}
      <div className="mb-6 border-b border-slate-200">
        <div className="flex gap-4">
          <button
            onClick={() => {
              if (activeTab === 'US') return; // Already on this tab
              if (hasUnsavedChanges) {
                if (confirm('יש לך שינויים שלא נשמרו. האם אתה בטוח שברצונך להמשיך?')) {
                  setActiveTab('US');
                }
              } else {
                setActiveTab('US');
              }
            }}
            className={`pb-3 px-4 font-semibold transition-colors border-b-2 ${
              activeTab === 'US'
                ? 'text-indigo-600 border-indigo-600'
                : 'text-slate-500 border-transparent hover:text-slate-700'
            }`}
          >
            אולטרסאונד (US)
          </button>
          <button
            onClick={() => {
              if (activeTab === 'Xray') return; // Already on this tab
              if (hasUnsavedChanges) {
                if (confirm('יש לך שינויים שלא נשמרו. האם אתה בטוח שברצונך להמשיך?')) {
                  setActiveTab('Xray');
                }
              } else {
                setActiveTab('Xray');
              }
            }}
            className={`pb-3 px-4 font-semibold transition-colors border-b-2 ${
              activeTab === 'Xray'
                ? 'text-indigo-600 border-indigo-600'
                : 'text-slate-500 border-transparent hover:text-slate-700'
            }`}
          >
            צילום רנטגן (X-Ray)
          </button>
        </div>
      </div>

      {/* Unsaved Changes Warning Banner */}
      {hasUnsavedChanges && !savingData && (
        <div className="mb-4 p-4 bg-amber-50 border-2 border-amber-400 text-amber-800 rounded-lg flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-semibold">יש לך שינויים שלא נשמרו</p>
              <p className="text-sm text-amber-700">אל תשכח לשמור את השינויים שלך</p>
            </div>
          </div>
          <button 
            onClick={handleSaveSchedule}
            className="px-6 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition-colors shadow-md flex items-center gap-2"
          >
            שמור עכשיו
          </button>
        </div>
      )}

      {/* First Time Setup Banner */}
      {isFirstTime && !hasUnsavedChanges && (
        <div className="mb-4 p-4 bg-blue-50 border-2 border-blue-400 text-blue-800 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-semibold">זו ההגדרה הראשונית שלך</p>
              <p className="text-sm text-blue-700">סקור את שעות העבודה המוצעות ושמור אותן או ערוך לפי הצורך</p>
            </div>
          </div>
          <button 
            onClick={handleSaveSchedule}
            disabled={savingData}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {savingData && (
              <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {savingData ? 'שומר...' : 'שמור הגדרות'}
          </button>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {saveSuccess && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">
          לוח הזמנים נשמר בהצלחה!
        </div>
      )}

      {loadingData ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(7)].map((_, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md p-6 animate-pulse">
              <div className="flex justify-between items-center mb-4">
                <div className="h-6 bg-slate-200 rounded w-24"></div>
                <div className="h-8 bg-slate-200 rounded-full w-14"></div>
              </div>
              <div className="space-y-3">
                <div className="h-12 bg-slate-100 rounded-lg"></div>
                <div className="h-12 bg-slate-100 rounded-lg"></div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
        {[...schedule].sort((a, b) => {
          // Maintain week day order
          const orderA = WEEK_DAYS.findIndex(d => d.id === a.dayId);
          const orderB = WEEK_DAYS.findIndex(d => d.id === b.dayId);
          return orderA - orderB;
        }).map((day) => {
          try {
            return (
          <div key={day.dayId} className={`bg-white rounded-lg shadow-md overflow-hidden border-2 transition-all ${
            day.isOpen ? 'border-indigo-100' : 'border-slate-200 opacity-70'
          }`}>
            {/* Day Header - Horizontal Layout */}
            <div className={`px-6 py-3 flex items-center justify-between ${
              day.isOpen ? 'bg-gradient-to-r from-indigo-50 to-purple-50 border-b-2 border-indigo-100' : 'bg-slate-100 border-b-2 border-slate-200'
            }`}>
              <div className="flex items-center gap-4">
                <span className="text-2xl">{day.isOpen ? '📅' : '🔒'}</span>
                <h3 className="text-lg font-bold text-slate-800">{day.dayLabel}</h3>
              </div>
              <label className="flex items-center cursor-pointer gap-2">
                <span className={`text-sm font-semibold ${day.isOpen ? 'text-indigo-600' : 'text-slate-500'}`}>
                  {day.isOpen ? 'פעיל' : 'סגור'}
                </span>
                <input
                  type="checkbox"
                  checked={day.isOpen}
                  onChange={() => toggleDay(day.dayId)}
                  className="sr-only peer"
                />
                <div className="relative w-12 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
              </label>
            </div>
            
            <div className="px-6 py-4">
            {day.isOpen ? (
              <div className="space-y-3">
                {/* Working Hours Sections - Horizontal Cards */}
                {(day.workingHours && day.workingHours.length > 0) ? (
                  day.workingHours.map((period, periodIndex) => (
                  <div key={periodIndex} className="border border-slate-200 rounded-lg p-4 bg-slate-50/50 hover:bg-slate-50 transition-colors">
                    <div className="flex items-start gap-6">
                      {/* Time Section */}
                      <div className="flex items-center gap-3">
                        <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="flex items-center gap-2">
                          <input
                            type="time"
                            value={period?.startTime || '08:00'}
                            onChange={(e) => updateWorkingHours(day.dayId, periodIndex, 'startTime', e.target.value)}
                            className="px-2 py-1 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          />
                          <span className="text-slate-400 font-bold">-</span>
                          <input
                            type="time"
                            value={period?.endTime || '17:00'}
                            onChange={(e) => updateWorkingHours(day.dayId, periodIndex, 'endTime', e.target.value)}
                            className="px-2 py-1 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                      </div>

                      {/* Divider */}
                      <div className="h-10 w-px bg-slate-300"></div>

                      {/* Technician Section */}
                      <div className="flex-1">
                        {(() => {
                          try {
                            const currentTech = getCurrentTechnician(period);
                            const futureChanges = getFutureTechnicianChanges(period);

                            return (
                              <div className="space-y-2">
                                {/* Current Technician */}
                                {currentTech ? (
                                  <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-full bg-green-500 flex items-center justify-center text-white text-sm font-bold shadow-sm">
                                      {currentTech.technicianName?.charAt(0) || '?'}
                                    </div>
                                    <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="font-semibold text-slate-800">{currentTech.technicianName}</span>
                                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">פעיל</span>
                                    </div>
                                    <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-600">
                                      <span>קיבולת: {currentTech.defaultCapacity}</span>
                                      <span>•</span>
                                      <span>קצב: {currentTech.intervalMinutes} דק'</span>
                                      {currentTech.slotCapacityOverrides && Object.keys(currentTech.slotCapacityOverrides).length > 0 && (
                                        <>
                                          <span>•</span>
                                          <span className="text-amber-600 font-medium">{Object.keys(currentTech.slotCapacityOverrides).length} עדכונים</span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 text-slate-400">
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                  <span className="text-sm">לא מוקצה טכנאי</span>
                                </div>
                              )}

                              {/* Future Changes */}
                              {futureChanges.map((futureTech) => (
                                <div key={futureTech.effectiveDate} className="flex items-center gap-3 border-r-2 border-blue-400 pr-2">
                                  <div className="w-9 h-9 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-bold shadow-sm">
                                    {futureTech.technicianName.charAt(0)}
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="font-semibold text-slate-800">{futureTech.technicianName}</span>
                                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                                        📅 {new Date(futureTech.effectiveDate).toLocaleDateString('he-IL')}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-600">
                                      <span>קיבולת: {futureTech.defaultCapacity}</span>
                                      <span>•</span>
                                      <span>קצב: {futureTech.intervalMinutes} דק'</span>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => removeTechnicianAssignment(day.dayId, periodIndex, futureTech.effectiveDate)}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                    title="בטל שינוי"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              ))}
                            </div>
                          );
                        } catch (error) {
                          console.error('Error rendering technician section:', error);
                          return (
                            <div className="text-sm text-red-600">
                              שגיאה בטעינת מידע טכנאי
                            </div>
                          );
                        }
                        })()}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openTechnicianModal(day.dayId, periodIndex)}
                          className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 flex items-center gap-1.5 font-medium transition-colors"
                          title={getCurrentTechnician(period) ? 'שנה טכנאי' : 'הקצה טכנאי'}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          {getCurrentTechnician(period) ? 'שנה' : 'הקצה'}
                        </button>
                        {getCurrentTechnician(period) && (
                          <button
                            onClick={() => openSlotCapacityModal(day.dayId, periodIndex)}
                            className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1.5 font-medium transition-colors"
                            title="ערוך קיבולת לפי שעה"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            שעות
                          </button>
                        )}
                        {day.workingHours.length > 1 && (
                          <button
                            onClick={() => removeWorkingPeriod(day.dayId, periodIndex)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="הסר תקופה"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
                ) : (
                  <div className="text-center py-8 text-slate-400">
                    <p className="text-sm">לא הוגדרו שעות עבודה ליום זה</p>
                  </div>
                )}

                <button
                  onClick={() => addWorkingPeriod(day.dayId)}
                  className="w-full py-2 px-4 border-2 border-dashed border-slate-300 text-slate-600 rounded-lg hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50/50 transition-all text-sm font-medium"
                >
                  + הוסף תקופת עבודה
                </button>
              </div>
            ) : (
              <div className="text-center py-6 text-slate-400">
                <svg className="w-10 h-10 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                <p className="text-sm">המרפאה סגורה ביום זה</p>
              </div>
            )}
            </div>
          </div>
            );
          } catch (error) {
            console.error('Error rendering day:', day.dayId, error);
            return (
              <div key={day.dayId} className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                <p className="text-red-600">שגיאה בטעינת {day.dayLabel}</p>
              </div>
            );
          }
        })}
        </div>
      )}

      <div className="mt-6 bg-teal-50 border border-teal-200 rounded-lg p-4">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-teal-600 mt-0.5 ml-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div>
            <h4 className="font-semibold text-teal-900 mb-1">איך זה עובד?</h4>
            <ul className="text-sm text-teal-700 space-y-1">
              <li>• <strong>שעות עבודה:</strong> הגדר מתי המרפאה פתוחה והקצב בין תורים</li>
              <li>• <strong>הקצאת טכנאים:</strong> בחר אילו טכנאים עובדים ביום מסוים</li>
              <li>• <strong>קיבולת דינמית:</strong> כל טכנאי יכול לקבל קיבולת שונה ליום ספציפי</li>
              <li>• <strong>ניהול טכנאים:</strong> צור וערוך פרופילים של טכנאים ב"ניהול טכנאים"</li>
            </ul>
            <p className="text-xs text-teal-600 mt-2 bg-teal-100 p-2 rounded">
              💡 <strong>Tip:</strong> שינוי שם או פרטי טכנאי ב"ניהול טכנאים" יעדכן אוטומטית בכל הימים
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button 
          onClick={handleSaveSchedule}
          disabled={savingData || loadingData}
          className={`px-6 py-3 rounded-lg font-medium transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${
            hasUnsavedChanges || isFirstTime
              ? 'bg-indigo-600 text-white hover:bg-indigo-700 ring-2 ring-indigo-300 ring-offset-2 scale-105'
              : 'bg-slate-500 text-white hover:bg-slate-600'
          }`}
        >
          {savingData && (
            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )}
          {hasUnsavedChanges && !savingData && (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          )}
          {savingData ? 'שומר...' : hasUnsavedChanges ? 'שמור שינויים' : 'שמור'}
        </button>
      </div>

      {/* Technician Assignment Modal */}
      {showTechModal && selectedDay && selectedPeriodIndex !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-slate-800">
                    הקצה טכנאי - {schedule.find(d => d.dayId === selectedDay)?.dayLabel}
                  </h3>
                  <p className="text-sm text-slate-600 mt-1">
                    תקופה: {schedule.find(d => d.dayId === selectedDay)?.workingHours[selectedPeriodIndex].startTime} - 
                    {schedule.find(d => d.dayId === selectedDay)?.workingHours[selectedPeriodIndex].endTime}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowTechModal(false);
                    setSelectedPeriodIndex(null);
                  }}
                  className="p-1 hover:bg-slate-100 rounded"
                >
                  <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 max-h-96 overflow-y-auto">
              {availableTechnicians.length > 0 ? (
                <div className="space-y-2">
                  {availableTechnicians.map((tech) => {
                    const day = schedule.find(d => d.dayId === selectedDay);
                    const period = day?.workingHours[selectedPeriodIndex];
                    const currentTech = getCurrentTechnician(period!);
                    const isAssigned = currentTech?.technicianId === tech.id;

                    return (
                      <button
                        key={tech.id}
                        onClick={() => selectTechnicianForAssignment(tech)}
                        className={`w-full p-4 rounded-lg border-2 text-right transition-all ${
                          isAssigned
                            ? 'border-green-300 bg-green-50 cursor-pointer hover:bg-green-100'
                            : 'border-slate-200 hover:border-indigo-400 hover:bg-indigo-50 cursor-pointer'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isAssigned ? 'bg-green-600' : 'bg-indigo-600'
                          }`}>
                            {isAssigned ? (
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <span className="text-white font-bold">{tech.name.charAt(0)}</span>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-slate-800">{tech.name}</p>
                            <p className="text-sm text-slate-600">
                              קיבולת ברירת מחדל: {tech.defaultConcurrency}
                            </p>
                          </div>
                          {isAssigned && (
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                              מוקצה כרגע
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <svg className="w-16 h-16 mx-auto text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  <p className="text-slate-500 text-lg">אין טכנאים זמינים</p>
                  <p className="text-slate-400 text-sm mt-2">
                    צור טכנאים חדשים ב"ניהול טכנאים"
                  </p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end">
              <button
                onClick={() => {
                  setShowTechModal(false);
                  setSelectedPeriodIndex(null);
                }}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
              >
                סגור
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Slot Capacity Override Modal */}
      {showSlotCapacityModal && editingTechnician && (() => {
        const { dayId, periodIndex } = editingTechnician;
        const day = schedule.find(d => d.dayId === dayId);
        const period = day?.workingHours[periodIndex];
        const currentTech = getCurrentTechnician(period!);
        const timeSlots = period && currentTech ? generateTimeSlots(period.startTime, period.endTime, currentTech.intervalMinutes) : [];

        if (!currentTech) return null;

        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 sticky top-0">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-white">
                      קיבולת לפי שעה - {currentTech.technicianName}
                    </h3>
                    <p className="text-sm text-blue-100 mt-1">
                      {day?.dayLabel} | {period?.startTime} - {period?.endTime}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowSlotCapacityModal(false);
                      setEditingTechnician(null);
                    }}
                    className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                  >
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm font-bold text-blue-800">
                      קיבולת ברירת מחדל: {currentTech.defaultCapacity} תורים בו-זמנית
                    </p>
                  </div>
                  <p className="text-xs text-blue-700">
                    כל השעות מתחילות עם קיבולת ברירת מחדל. ערוך שעות ספציפיות כדי לשנות.
                  </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {timeSlots.map((slot) => {
                    const currentOverride = currentTech.slotCapacityOverrides?.[slot];
                    const displayValue = currentOverride !== undefined ? currentOverride : currentTech.defaultCapacity;

                    return (
                      <div
                        key={slot}
                        className={`p-3 rounded-lg border-2 ${
                          currentOverride !== undefined
                            ? 'border-amber-400 bg-amber-50'
                            : 'border-slate-200 bg-white'
                        }`}
                      >
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          {slot}
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="0"
                            max="10"
                            value={displayValue}
                            onChange={(e) => {
                              const newValue = parseInt(e.target.value);
                              if (!isNaN(newValue) && newValue >= 0 && newValue <= 10) {
                                updateSlotCapacity(
                                  dayId,
                                  periodIndex,
                                  slot,
                                  newValue === 0 ? undefined : newValue
                                );
                              }
                            }}
                            className="w-16 px-2 py-1 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-indigo-500"
                          />
                          {currentOverride !== undefined && (
                            <button
                              onClick={() => updateSlotCapacity(dayId, periodIndex, slot, undefined)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                              title="איפוס לברירת מחדל"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                        {currentOverride !== undefined && (
                          <p className="text-xs text-amber-700 mt-1">
                            (ברירת מחדל: {period?.defaultCapacity || 1})
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="p-6 border-t-2 border-slate-200 bg-gradient-to-r from-slate-50 to-slate-100 flex justify-between items-center">
                <div className="text-sm text-slate-700">
                  {currentTech.slotCapacityOverrides && Object.keys(currentTech.slotCapacityOverrides).length > 0 ? (
                    <span className="text-amber-700 font-bold flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      ⚠️ {Object.keys(currentTech.slotCapacityOverrides).length} שעות עם עדכון קיבולת
                    </span>
                  ) : (
                    <span className="text-slate-500 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      כל השעות משתמשות בברירת מחדל ({currentTech.defaultCapacity})
                    </span>
                  )}
                </div>
                <button
                  onClick={() => {
                    setShowSlotCapacityModal(false);
                    setEditingTechnician(null);
                  }}
                  className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-bold transition-all shadow-md hover:shadow-lg"
                >
                  סיום ✓
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Effective Date Modal */}
      {showEffectiveDateModal && selectedTechnicianForAssignment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" dir="rtl">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6">
              <h3 className="text-xl font-bold text-white">בחר תאריך תחילת תוקף</h3>
              <p className="text-sm text-indigo-100 mt-1">
                טכנאי: {selectedTechnicianForAssignment.name}
              </p>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  תאריך תחילה
                </label>
                <input
                  type="date"
                  value={effectiveDate}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  קצב תורים (דקות)
                </label>
                <select
                  value={intervalMinutes}
                  onChange={(e) => setIntervalMinutes(parseInt(e.target.value))}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                >
                  <option value={5}>5 דקות</option>
                  <option value={10}>10 דקות</option>
                  <option value={15}>15 דקות</option>
                  <option value={20}>20 דקות</option>
                  <option value={30}>30 דקות</option>
                  <option value={60}>60 דקות</option>
                </select>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  💡 <strong>שינוי מיידי:</strong> בחר תאריך של היום
                </p>
                <p className="text-sm text-blue-800 mt-1">
                  📅 <strong>שינוי עתידי:</strong> בחר תאריך עתידי כדי לתזמן את השינוי מראש
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex gap-3">
              <button
                onClick={() => {
                  setShowEffectiveDateModal(false);
                  setSelectedTechnicianForAssignment(null);
                  setEffectiveDate('');
                  setShowTechModal(true); // Back to technician selection
                }}
                className="flex-1 px-6 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 font-medium"
              >
                ביטול
              </button>
              <button
                onClick={confirmTechnicianAssignment}
                disabled={!effectiveDate}
                className="flex-1 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                אשר הקצאה
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Schedule;
